import { useState, useRef, useEffect } from 'react'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { useDataset } from '@hooks/useCatalog'
import Markdown from 'markdown-to-jsx'
import PrivacyTag from '@components/PrivacyTag/PrivacyTag'

import './DataSetAbout.css'

interface DataSetAboutProps {
  id: string
}

export default function DataSetAbout({ id }: DataSetAboutProps) {
  const { dataset, owner } = useDataset(id)
  const [isExpanded, setIsExpanded] = useState(false)

  const [canExpand, setCanExpand] = useState(false)
  const descriptionRef = useRef<HTMLDivElement>(null)

  const isActive = true
  let privacy = { level: 'private', grantedBy: '' }
  switch (dataset.schema) {
    case 'public_data':
      privacy = { ...privacy, level: 'public' }
      break
  }
  useEffect(() => {
    const element = descriptionRef.current
    if (element) {
      const hasOverflow = element.scrollHeight > 105
      setCanExpand(hasOverflow)
    }
  }, [dataset.description])

  const vid =
    dataset.connectorId && `${dataset.connector}:${dataset.connectorId}`

  return (
    <section id="dataset-about">
      <h2 className="about-title">
        <FormattedMessage id="dataset.about.title" />
      </h2>
      <div className="dataset-body">
        <div className="about-grid">
          <strong className="about-label">
            <FormattedMessage id="dataset.about.status" />
          </strong>
          <div className="about-value status-value">
            <span>
              {isActive ? (
                <FormattedMessage id="dataset.status.active" />
              ) : (
                <FormattedMessage id="dataset.status.inactive" />
              )}
            </span>
            {isActive && <span className="icon icon-circle-check" />}
          </div>
          <strong className="about-label">
            <FormattedMessage id="dataset.about.privacy" />
          </strong>
          <div className="about-value privacy-value">
            <PrivacyTag privacy={privacy.level as 'public' | 'private'} />
            {privacy.level === 'limited' && (
              <span className="privacy-explanation">
                (
                <FormattedMessage
                  id="dataset.privacy.limited_reason"
                  values={{
                    grantedBy: (
                      <strong className="granted-by-link" key="grantedByLink">
                        {privacy.grantedBy}
                      </strong>
                    )
                  }}
                />
                )
              </span>
            )}
          </div>
          {vid && (
            <>
              <strong className="about-label">
                <FormattedMessage id="dataset.about.id" />
              </strong>
              <div className="about-value status-value">
                <span>{vid}</span>
              </div>
            </>
          )}
          {owner && (
            <>
              <strong className="about-label">
                <FormattedMessage id="dataset.about.owner" />
              </strong>
              <div className="about-value status-value">
                <span>
                  {owner?.name} - ({owner?.nif})
                </span>
              </div>
            </>
          )}
          <strong className="about-label">
            <FormattedMessage id="dataset.about.update" />
          </strong>
          <div className="about-value status-value">
            {dataset.lastUpdate && <FormattedDate value={dataset.lastUpdate} />}
            {!dataset.lastUpdate && (
              <FormattedMessage id="dataset.about.update.unknown" />
            )}
          </div>
        </div>

        <div className="dataset-description">
          <h3>
            <FormattedMessage id="dataset.description.title" />
          </h3>

          <div
            ref={descriptionRef}
            className={`description-wrapper ${canExpand ? (isExpanded ? 'expanded' : 'collapsed') : ''}`}
          >
            <Markdown>{dataset.description}</Markdown>
          </div>

          {canExpand && (
            <button
              type="button"
              className="read-more-button"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <FormattedMessage
                id={
                  isExpanded
                    ? 'dataset.description.less'
                    : 'dataset.description.more'
                }
              />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
